import { useEffect, useMemo, useState } from 'react';
import { Check, MagnifyingGlass, Path, Plus } from '@phosphor-icons/react';
import RouteMapPreview from '../RouteMapPreview';
import MetroMark from '../MetroMark';
import SortableStopList from './SortableStopList';
import telegramService from '../../services/telegram';
import { METRO_LIST } from '../../utils/metro';
import { orderByProximity, straightLength } from '../../utils/routeOrder';
import { fetchOsrmRoute, formatDist, formatWalkTime } from '../../utils/walk';
import { plural } from '../../utils/plural';

const PLACES = ['место', 'места', 'мест'];

// Название из админки бывает с «ё» и без — ищем без разницы
const norm = (s) => String(s || '').toLowerCase().replace(/ё/g, 'е');

/**
 * Редактор своего маршрута: «Ваш маршрут» в мастере и «Изменить маршрут»
 * на прогулке — один и тот же экран.
 *
 * Сверху карта и итог «3 места · ~2,4 км · 35 мин» — маршрут собирается
 * не вслепую. Ниже старт, список мест с перетаскиванием за ручку,
 * «Упорядочить по пути» и название.
 *
 * - `stops` — `[{ placeId, title, thumb, latitude, longitude, metro, ... }]`,
 *   `onChange(stops)`;
 * - `station` — `{ name, lat, lng }`, `onStationChange(station)`: откуда
 *   вести путь к первому месту, пока нет геолокации;
 * - `title`, `onTitleChange` — нет обработчика, нет и поля;
 * - `lockedCount` — уже пройденные места сверху: их не двигают;
 * - `onAddPlaces` — кнопка «Добавить места».
 */
const RouteEditor = ({
  stops, onChange, station, onStationChange, title, onTitleChange,
  lockedCount = 0, onAddPlaces,
}) => {
  const [pickStation, setPickStation] = useState(false);
  const [stationQuery, setStationQuery] = useState('');
  const [path, setPath] = useState(null);

  // Пеший путь по местам — для линии на карте и честной длины. Строится
  // с задержкой: пока человек переставляет места, запросы не копятся
  const points = useMemo(
    () => stops.filter((s) => s.latitude != null && s.longitude != null).map((s) => [s.latitude, s.longitude]),
    [stops]
  );
  const pointsKey = JSON.stringify(points);
  useEffect(() => {
    setPath(null);
    if (points.length < 2) return undefined;
    let cancelled = false;
    const timer = setTimeout(() => {
      fetchOsrmRoute(points).then((p) => { if (!cancelled && p) setPath(p); });
    }, 400);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [pointsKey]); // eslint-disable-line react-hooks/exhaustive-deps

  // Длина: по пешему пути, пока его нет — по прямой. Знак «~» в обоих
  // случаях: это прикидка, а не измеренный маршрут (брендбук 6.2)
  const lengthM = path ? straightLength(path) : straightLength(points);
  const facts = [
    `${stops.length} ${plural(stops.length, PLACES)}`,
    lengthM > 0 && formatDist(lengthM),
    lengthM > 0 && formatWalkTime(lengthM)?.replace(' пешком', ''),
  ].filter(Boolean);

  const move = (from, to) => {
    const next = [...stops];
    const [item] = next.splice(from, 1);
    next.splice(to, 0, item);
    onChange(next);
  };
  const remove = (i) => onChange(stops.filter((_, k) => k !== i));

  // Порядок «ближайшее — следующим» от старта. Пройденные места остаются
  // на своих местах, переставляется только то, что впереди
  const orderAlongWay = () => {
    telegramService.hapticFeedback('light');
    const locked = stops.slice(0, lockedCount);
    const rest = stops.slice(lockedCount);
    const last = locked[locked.length - 1];
    const from = last
      ? [last.latitude, last.longitude]
      : (station ? [station.lat, station.lng] : null);
    onChange([...locked, ...orderByProximity(rest, from)]);
  };

  const stations = METRO_LIST.filter((m) => norm(m.name).includes(norm(stationQuery)));

  return (
    <div className="flex flex-col gap-5">
      <div>
        <RouteMapPreview
          stops={stops.map((s) => ({ place: { latitude: s.latitude, longitude: s.longitude } }))}
          path={path}
        />
        <div className="mt-3 text-body text-theme-text-primary tabular">{facts.join(' · ')}</div>
      </div>

      {/* Старт — откуда вести к первому месту без геолокации. С ней путь
          и так строится от человека, поэтому выбор метро — запасной */}
      {lockedCount === 0 && (
        <section>
          <div className="mb-2 text-caption text-theme-text-secondary">Старт без геолокации</div>
          <button
            onClick={() => { telegramService.hapticFeedback('light'); setPickStation((v) => !v); }}
            aria-expanded={pickStation}
            className="flex min-h-[48px] w-full items-center gap-3 rounded-md border border-theme-border bg-theme-card px-3 text-left"
            style={{ WebkitTapHighlightColor: 'transparent' }}
          >
            <MetroMark />
            <span className="min-w-0 flex-1 truncate text-body text-theme-text-primary">
              {station ? station.name : 'Выбрать станцию'}
            </span>
            <span className="shrink-0 text-label text-action-text">{pickStation ? 'Готово' : 'Изменить'}</span>
          </button>
          {pickStation && (
            <div className="mt-2">
              <label className="flex h-12 items-center gap-2 rounded-md border border-theme-border-strong bg-theme-card px-3">
                <MagnifyingGlass size={20} className="shrink-0 text-theme-text-secondary" />
                <input
                  type="text"
                  value={stationQuery}
                  onChange={(e) => setStationQuery(e.target.value)}
                  placeholder="Станция метро"
                  aria-label="Поиск станции метро"
                  // 16px: меньший кегль на iOS зумит страницу при фокусе (брендбук 3.2)
                  className="min-w-0 flex-1 border-0 bg-transparent text-body text-theme-text-primary outline-none"
                />
              </label>
              <div className="mt-2 max-h-[240px] overflow-y-auto" style={{ overscrollBehavior: 'contain' }}>
                {stations.map((st) => {
                  const selected = station?.name === st.name;
                  return (
                    <button
                      key={st.id}
                      onClick={() => {
                        telegramService.hapticFeedback('light');
                        onStationChange({ name: st.name, lat: st.lat, lng: st.lng });
                        setPickStation(false);
                        setStationQuery('');
                      }}
                      className={`flex min-h-[44px] w-full items-center gap-3 rounded-md border-0 px-3 text-left text-body ${
                        selected ? 'bg-theme-bg-secondary text-theme-text-primary' : 'bg-transparent text-theme-text-primary'}`}
                    >
                      <span className="min-w-0 flex-1 truncate">{st.name}</span>
                      {selected && <Check size={18} weight="bold" className="shrink-0 text-action-text" />}
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </section>
      )}

      <section>
        <div className="mb-2 flex items-center justify-between gap-3">
          <h3 className="m-0 font-display text-h3 text-theme-text-primary">Порядок</h3>
          {stops.length - lockedCount > 1 && (
            <button
              onClick={orderAlongWay}
              className="flex h-10 items-center gap-1.5 rounded-full border-0 bg-theme-bg-secondary px-3 text-body-sm text-action-text"
              style={{ WebkitTapHighlightColor: 'transparent' }}
            >
              <Path size={16} className="shrink-0" />
              Упорядочить по пути
            </button>
          )}
        </div>
        <SortableStopList
          items={stops.map((s) => ({
            key: s.placeId,
            title: s.title,
            thumb: s.thumb,
            subtitle: s.metro ? `м. ${s.metro}` : s.address,
          }))}
          onReorder={move}
          onRemove={remove}
          lockedCount={lockedCount}
          minCount={Math.max(2, lockedCount + 1)}
        />
        {onAddPlaces && (
          <button
            onClick={() => { telegramService.hapticFeedback('light'); onAddPlaces(); }}
            className="mt-2 flex h-12 w-full items-center justify-center gap-2 rounded-md border-0 bg-theme-card-secondary text-label text-action-text"
            style={{ WebkitTapHighlightColor: 'transparent' }}
          >
            <Plus size={20} />
            Добавить места
          </button>
        )}
      </section>

      {onTitleChange && (
        <section>
          <label htmlFor="route-title" className="mb-2 block text-caption text-theme-text-secondary">Название</label>
          <input
            id="route-title"
            type="text"
            maxLength={80}
            value={title}
            onChange={(e) => onTitleChange(e.target.value)}
            className="h-12 w-full rounded-md border border-theme-border-strong bg-theme-card px-3 text-body text-theme-text-primary outline-none focus:border-action"
          />
        </section>
      )}
    </div>
  );
};

export default RouteEditor;
